import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import { alertError } from "../../../../utils/logger";
import { getTasarReloj, searchWatchesForTasacion } from "../../../../api/reloj";
import {
  CardContent,
  Typography,
  TextField,
  Grid,
} from "@mui/material";
import { Autocomplete } from "@material-ui/lab";
import { Button, CircularProgress, makeStyles } from "@material-ui/core";
import { TarjetaModelo } from "../../../components/TarjetaModelo";

/** Normalizar para clave de agrupación/deduplicación (igual que en la web) */
function normalizeKeyPart(str) {
  const s = String(str || "").trim().toLowerCase();
  return s.replace(/[\s\u00A0\u200B-\u200D\uFEFF]+/g, "").trim() || "";
}

/** Formatear datos de modelo desde relojes (igual que en Tasacion.jsx de la web) */
function getData(modelos) {
  for (const modelo of modelos) {
    let brandName = "";
    if (modelo.marcaInfo) {
      if (typeof modelo.marcaInfo === "object") {
        const rawBrand = modelo.marcaInfo.nombre || modelo.marcaInfo.name || "";
        brandName = String(rawBrand).trim();
      } else if (typeof modelo.marcaInfo === "string") {
        brandName = String(modelo.marcaInfo).trim();
      }
    }
    if (!brandName && modelo.marca) {
      if (typeof modelo.marca === "object") {
        const rawBrand = modelo.marca.nombre || modelo.marca.name || "";
        if (rawBrand) brandName = String(rawBrand).trim();
      }
    }
    brandName = brandName.replace(/undefined/gi, "").replace(/null/gi, "").trim();

    const modelName = String(modelo.nombre || "").trim().replace(/undefined/gi, "").replace(/null/gi, "").trim();

    if (!brandName && modelo.sku) {
      const skuParts = String(modelo.sku).trim().split(/\s+/);
      if (skuParts.length > 0 && !/^\d+$/.test(skuParts[0])) {
        brandName = skuParts[0];
      }
    }

    const capitalizeWords = (str) => {
      if (!str) return "";
      return str.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
    };

    modelo.nombreSelected = [brandName, modelName].filter(Boolean).join(" ");
    const rawTitle = [brandName, modelName].filter(Boolean).join(" ") || modelName || "Modelo sin nombre";
    modelo.displayTitle = capitalizeWords(rawTitle);

    const isManual = String(modelo.origin || "").toLowerCase() === "manual";
    const baseSku = isManual
      ? (modelo.n_mero_de_referencia || modelo.sampleSku || modelo.sku || "")
      : (modelo.sampleSku || modelo.sku || "");
    let skuDisplay = String(baseSku).trim();

    if (skuDisplay && brandName) {
      const brandLower = brandName.toLowerCase();
      if (skuDisplay.toLowerCase().startsWith(brandLower)) {
        skuDisplay = skuDisplay.substring(brandName.length).trim();
      }
    }
    if (skuDisplay) {
      const parts = skuDisplay.split(/\s+/);
      if (parts.length > 1) skuDisplay = parts[parts.length - 1];
    }

    const invalidWords = ["steel", "dial", "tone", "new", "papers", "lines", "date", "ref", "reference", "roman", "arabic", "index", "diamond", "bezel", "bracelet", "leather", "rubber"];
    if (invalidWords.includes(skuDisplay.toLowerCase())) skuDisplay = "";
    if (skuDisplay && /^\d+mm?$/i.test(skuDisplay)) skuDisplay = "";
    if (skuDisplay && modelo.diametro) {
      const diametroStr = String(modelo.diametro).toLowerCase().trim();
      const diametroNum = diametroStr.replace(/mm/gi, "").trim();
      if (skuDisplay.toLowerCase() === diametroNum || skuDisplay.toLowerCase() === diametroStr) skuDisplay = "";
    }
    if (skuDisplay && !/\d/.test(skuDisplay) && skuDisplay.length <= 2) skuDisplay = "";

    modelo.skuDisplay = skuDisplay || "";
    modelo._hasValidSku = !!skuDisplay && skuDisplay.length > 0;

    const normalizeForDedup = (str) => String(str || "").toLowerCase().trim().replace(/\s+/g, " ");
    modelo._brandForDedup = normalizeForDedup(brandName);
    modelo._skuForDedup = (skuDisplay && String(skuDisplay).trim()) ? normalizeForDedup(skuDisplay) : normalizeForDedup(baseSku);
  }
  return modelos;
}

const initialTasacion = { min: 0, avg: 0, max: 0, contenido: "", modelo: "", estado: "" };
const limit = 50;

const useStyles = makeStyles(() => ({
  dialogContent: { padding: 0, overflow: "hidden" },
  fancyLoader: { color: "#AF9A51", animation: `$spin 1s linear infinite` },
  "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
}));

const contenidoOptions = [
  { id: 1, text: "Solo el reloj" },
  { id: 2, text: "Reloj con estuche original" },
  { id: 3, text: "Reloj con documentos originales" },
  { id: 4, text: "Reloj con estuche y documentos originales" },
];

/** Extraer opciones de filtro de los relojes (igual que en la web) */
function extractFilterOptions(relojes) {
  if (!relojes || !Array.isArray(relojes)) return { materialesCaja: [], esferas: [], diametros: [] };
  const materialesSet = new Set();
  const esferasSet = new Set();
  const diametrosSet = new Set();
  const normalizeValue = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return String(v).trim();
    if (typeof v === "string") {
      const n = v.trim().replace(/\s+/g, " ");
      return n || null;
    }
    return String(v).trim();
  };
  relojes.forEach((reloj) => {
    let material = normalizeValue(reloj.specifications?.material_de_la_caja) ||
      normalizeValue(reloj.specifications?.material_caja) ||
      normalizeValue(reloj.specifications?.material) ||
      normalizeValue(reloj.data?.specifications?.material_de_la_caja) ||
      normalizeValue(reloj.data?.specifications?.material_caja) ||
      normalizeValue(reloj.data?.specifications?.material);
    if (material) materialesSet.add(material);

    let esfera = normalizeValue(reloj.specifications?.esfera) ||
      normalizeValue(reloj.specifications?.colorEsfera) ||
      normalizeValue(reloj.specifications?.color_esfera) ||
      normalizeValue(reloj.data?.specifications?.esfera) ||
      normalizeValue(reloj.data?.specifications?.colorEsfera) ||
      normalizeValue(reloj.data?.specifications?.color_esfera);
    if (esfera) esferasSet.add(esfera);

    const diametroRaw = reloj.specifications?.di_metro;
    if (diametroRaw != null && diametroRaw !== "") {
      let d = normalizeValue(diametroRaw);
      if (d) {
        const m = d.match(/(\d+(?:[.,]\d+)?)/);
        if (m) {
          const num = parseFloat(m[1].replace(",", "."));
          if (!isNaN(num)) {
            const numStr = num % 1 === 0 ? String(num) : num.toFixed(1).replace(/\.0$/, "");
            diametrosSet.add(`${numStr} mm`);
          }
        }
      }
    }
  });
  return {
    materialesCaja: Array.from(materialesSet).sort(),
    esferas: Array.from(esferasSet).sort(),
    diametros: Array.from(diametrosSet).sort((a, b) => {
      const na = parseFloat(a);
      const nb = parseFloat(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return String(a).localeCompare(b);
    }),
  };
}

export default function ValorarRelojesPage() {
  const [modelos, setModelos] = useState([]);
  const [tasacion, setTasacion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedZonas, setSelectedZonas] = useState([]);
  const [selectedModelo, setSelectedModelo] = useState(null);
  const [selectedEstado, setSelectedEstado] = useState("");
  const [selectedContenido, setSelectedContenido] = useState("");
  const [selectedMaterialCaja, setSelectedMaterialCaja] = useState("");
  const [selectedEsfera, setSelectedEsfera] = useState("");
  const [selectedDiametro, setSelectedDiametro] = useState("");
  const [availableFilters, setAvailableFilters] = useState({ materialesCaja: [], esferas: [], diametros: [] });
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const lastRequestIdRef = useRef(0);
  const lastQueriedTermRef = useRef("");
  const classes = useStyles();

  const searchModelos = useCallback(
    async (searchValue, resetPage = true) => {
      if (loading && !loadingMore && !resetPage) return;
      const pageToLoad = resetPage ? 1 : page + 1;

      if (resetPage) {
        setLoading(true);
        setPage(1);
        setCurrentSearchTerm(searchValue);
      } else {
        setLoadingMore(true);
      }
      const thisRequestId = ++lastRequestIdRef.current;
      let effectiveSearchValue = searchValue;
      lastQueriedTermRef.current = searchValue;

      try {
        let res = await searchWatchesForTasacion(searchValue, pageToLoad, limit);
        let relojesApi = res.data?.data || [];

        const tryFallbackSearch = async (fallbackTerm) => {
          const resFb = await searchWatchesForTasacion(fallbackTerm, pageToLoad, limit);
          const relojesFb = resFb.data?.data || [];
          if (relojesFb?.length > 0) {
            return { res: resFb, relojesApi: relojesFb, term: fallbackTerm };
          }
          return null;
        };

        if (resetPage && (!relojesApi || relojesApi.length === 0) && searchValue?.trim()) {
          const parts = searchValue.trim().split(/\s+/).filter(Boolean);
          let fallbackResult = null;
          if (parts.length > 1) {
            const numericPart = parts.find((p) => /^\d{3,}/.test(p));
            const textRest = parts.slice(1).join(" ");
            if (numericPart) {
              fallbackResult = await tryFallbackSearch(numericPart);
            }
            if (!fallbackResult && textRest) {
              fallbackResult = await tryFallbackSearch(textRest);
            }
          }
          if (fallbackResult) {
            res = fallbackResult.res;
            relojesApi = fallbackResult.relojesApi;
            effectiveSearchValue = fallbackResult.term;
            lastQueriedTermRef.current = fallbackResult.term;
          }
        }

        const processRelojesToModelos = (relojes) => {
          const grp = new Map();
          (relojes || []).forEach((reloj) => {
            const brandObj = reloj.brand;
            const brandName = (brandObj?.nombre || brandObj?.name || "").toString().trim();
            const sku = (reloj.sku || "").toString().trim();
            if (!brandName || !sku) return;
            const key = `${normalizeKeyPart(brandName)}|${normalizeKeyPart(sku)}`;
            if (!key || key === "|") return;
            if (!grp.has(key)) grp.set(key, { relojes: [], brandName, sku });
            grp.get(key).relojes.push(reloj);
          });
          const groupedWatches = Array.from(grp.values()).map((group) => {
            const sorted = group.relojes.sort((a, b) => {
              const skuA = (a.sku || "").toString().trim();
              const skuB = (b.sku || "").toString().trim();
              const aIsNumeric = /^\d+$/.test(skuA);
              const bIsNumeric = /^\d+$/.test(skuB);
              if (aIsNumeric && !bIsNumeric) return -1;
              if (!aIsNumeric && bIsNumeric) return 1;
              if (skuA.length !== skuB.length) return skuA.length - skuB.length;
              return skuA.localeCompare(skuB);
            });
            const rep = sorted[0];
            const repSku = (rep.sku || "").toString().trim();
            const watchCount = rep.count || group.relojes.length;
            return {
              reloj: rep,
              modeloId: rep.specifications?.reloj_modelo?._id || rep.specifications?.reloj_modelo,
              brandName: group.brandName,
              sku: repSku,
              count: watchCount,
            };
          });
          const dedup = new Map();
          groupedWatches.forEach((item) => {
            const key = `${normalizeKeyPart(item.brandName)}|${normalizeKeyPart(item.sku)}`;
            if (!key || key === "|") return;
            const existing = dedup.get(key);
            if (!existing || (existing.count ?? 0) < (item.count ?? 0)) dedup.set(key, item);
          });
          const modelosLike = Array.from(dedup.values()).map((item) => {
            const reloj = item.reloj;
            const modeloObj = reloj.specifications?.reloj_modelo;
            const specs = reloj.specifications || {};
            return {
              _id: item.modeloId || reloj._id,
              nombre: modeloObj?.nombre || reloj.name || "",
              sku: item.sku,
              n_mero_de_referencia: (reloj.specifications?.n_mero_de_referencia || "").toString().trim() || undefined,
              origin: reloj.origin,
              diametro: specs.di_metro || specs.diametro,
              marcaInfo: reloj.brand,
              _count: item.count,
              materialCaja: specs.material_de_la_caja || specs.material_caja || specs.material,
              colorEsfera: specs.esfera || specs.colorEsfera || specs.color_esfera,
              urlImage: reloj.images?.[0] || reloj.coverImage,
            };
          });
          const formatted = getData(modelosLike);
          const withSku = formatted.filter(m => m._hasValidSku && (m._count || 0) >= 1);
          const canonicalKey = (m) => {
            const title = normalizeKeyPart(m.displayTitle);
            const sku = normalizeKeyPart(m.skuDisplay ?? m._skuForDedup);
            return `${title}|${sku}` || (m._id && String(m._id)) || "unknown";
          };
          const uniqueByCanonical = (items) => {
            const byKey = new Map();
            (items || []).forEach((it) => {
              const k = canonicalKey(it);
              const existing = byKey.get(k);
              if (!existing || (existing._count ?? 0) < (it._count ?? 0)) byKey.set(k, it);
            });
            return Array.from(byKey.values());
          };
          const sorted = withSku.sort((a, b) => {
            const countA = a._count || 0;
            const countB = b._count || 0;
            if (countA !== countB) return countB - countA;
            const aHasBrand = !!(a._brandForDedup && a._brandForDedup.trim());
            const bHasBrand = !!(b._brandForDedup && b._brandForDedup.trim());
            if (aHasBrand && !bHasBrand) return -1;
            if (!aHasBrand && bHasBrand) return 1;
            return 0;
          });
          return uniqueByCanonical(sorted);
        };

        let finalModelos = processRelojesToModelos(relojesApi);
        if (resetPage && finalModelos.length === 0 && searchValue?.trim()) {
          const parts = searchValue.trim().split(/\s+/).filter(Boolean);
          if (parts.length > 1) {
            const numericPart = parts.find((p) => /^\d{3,}/.test(p));
            const textRest = parts.slice(1).join(" ");
            let fallbackResult = null;
            if (numericPart) {
              fallbackResult = await tryFallbackSearch(numericPart);
            }
            if (!fallbackResult && textRest) {
              fallbackResult = await tryFallbackSearch(textRest);
            }
            if (fallbackResult) {
              res = fallbackResult.res;
              relojesApi = fallbackResult.relojesApi;
              effectiveSearchValue = fallbackResult.term;
              lastQueriedTermRef.current = fallbackResult.term;
              finalModelos = processRelojesToModelos(fallbackResult.relojesApi);
            }
          }
        }

        if (thisRequestId !== lastRequestIdRef.current) {
          if (resetPage) setLoading(false);
          else setLoadingMore(false);
          return;
        }

        const uniqueByCanonical = (items) => {
          const canonicalKey = (m) => {
            const title = normalizeKeyPart(m.displayTitle);
            const sku = normalizeKeyPart(m.skuDisplay ?? m._skuForDedup);
            return `${title}|${sku}` || (m._id && String(m._id)) || "unknown";
          };
          const byKey = new Map();
          (items || []).forEach((it) => {
            const k = canonicalKey(it);
            const existing = byKey.get(k);
            if (!existing || (existing._count ?? 0) < (it._count ?? 0)) byKey.set(k, it);
          });
          return Array.from(byKey.values());
        };

        if (resetPage) {
          setModelos(finalModelos);
          setPage(1);
          setCurrentSearchTerm(effectiveSearchValue);
          if (!finalModelos || finalModelos.length === 0) setHasMore(false);
        } else {
          setModelos((prev) => {
            const prevList = prev || [];
            const combined = [...prevList, ...finalModelos];
            const sortedCombined = combined.sort((a, b) => {
              const countA = a._count || 0;
              const countB = b._count || 0;
              if (countA !== countB) return countB - countA;
              const aHasBrand = !!(a._brandForDedup && a._brandForDedup.trim());
              const bHasBrand = !!(b._brandForDedup && b._brandForDedup.trim());
              if (aHasBrand && !bHasBrand) return -1;
              if (!aHasBrand && bHasBrand) return 1;
              return 0;
            });
            const deduped = uniqueByCanonical(sortedCombined);
            if (deduped.length === prevList.length) {
              setHasMore(false);
              return prevList;
            }
            return deduped;
          });
          setPage(pageToLoad);
        }

        if (res?.data?.totalPages != null && res.data?.page != null) {
          setHasMore(res.data.totalPages > res.data.page);
        }
      } catch (error) {
        console.error("Error buscando relojes para tasación:", error);
        alertError({ error, customMessage: "No se pudieron cargar los modelos." });
      } finally {
        if (resetPage) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [loading, loadingMore, page]
  );

  useEffect(() => {
    searchModelos("", true);
  }, []);

  /** Cargar opciones de filtro (material, esfera, diámetro) al seleccionar un modelo */
  const loadFilterOptions = useCallback(async (modelo) => {
    if (!modelo?._id) return;
    setLoadingFilters(true);
    try {
      const obj = {
        modelo: modelo._id,
        estado: null,
        contenido: null,
        zona: [],
      };
      const res = await getTasarReloj(obj);
      const relojesParaFiltros = res.data?.allRelojesForFilters?.length
        ? res.data.allRelojesForFilters
        : res.data?.relojes || [];
      if (relojesParaFiltros.length > 0) {
        setAvailableFilters(extractFilterOptions(relojesParaFiltros));
      } else {
        setAvailableFilters({ materialesCaja: [], esferas: [], diametros: [] });
      }
    } catch (error) {
      console.error("Error cargando opciones de filtro:", error);
      setAvailableFilters({ materialesCaja: [], esferas: [], diametros: [] });
    } finally {
      setLoadingFilters(false);
    }
  }, []);

  const searchTimeoutRef = useRef(null);
  const handleInputChange = (event, value) => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (value.length >= 3) {
        searchModelos(value, true);
      } else if (value.length === 0) {
        searchModelos("", true);
      }
      searchTimeoutRef.current = null;
    }, 300);
  };

  const handleScroll = (event) => {
    const bottom = event.target?.scrollHeight - event.target?.scrollTop === event.target?.clientHeight;
    if (bottom && hasMore && !loadingMore) {
      searchModelos(currentSearchTerm || searchTerm, false);
    }
  };

  const handleTasar = async () => {
    if (!selectedModelo) {
      alertError({ customMessage: "Elegir el modelo es necesario" });
      return;
    }

    setTasacion(null);
    const contenido = () => {
      switch (selectedContenido?.id) {
        case 1: return { estuche: false, documento: false };
        case 2: return { estuche: true, documento: false };
        case 3: return { estuche: false, documento: true };
        case 4: return { estuche: true, documento: true };
        default: return null;
      }
    };
    const obj = {
      modelo: selectedModelo._id,
      estado: selectedEstado === "Usado" ? true : selectedEstado ? false : null,
      contenido: contenido(),
      zona: selectedZonas,
    };
    if (selectedMaterialCaja) obj.materialCaja = selectedMaterialCaja;
    if (selectedEsfera) obj.esfera = selectedEsfera;
    if (selectedDiametro) obj.diametro = selectedDiametro;

    try {
      const res = await getTasarReloj(obj);
      const relojesParaFiltros = res.data?.allRelojesForFilters?.length
        ? res.data.allRelojesForFilters
        : res.data?.relojes || [];
      if (relojesParaFiltros.length > 0) {
        setAvailableFilters(extractFilterOptions(relojesParaFiltros));
      } else {
        setAvailableFilters({ materialesCaja: [], esferas: [], diametros: [] });
      }
      const newTasacion = {
        ...initialTasacion,
        modelo: selectedModelo.displayTitle || selectedModelo.nombreSelected || selectedModelo.nombre,
        estado: selectedEstado,
        contenido: selectedContenido?.text || "",
        min: res.data.min,
        avg: res.data.avg,
        max: res.data.max,
      };
      setTasacion(newTasacion);
    } catch (error) {
      alertError({ error, customMessage: "Error al tasar el reloj." });
    }
  };

  return (
    <>
      <Card>
        <CardHeader title="Tasar Relojes" />
        <CardBody>
          <div className="row d-flex align-items-center">
            <div className="col-12 col-md-12 text-center my-2">
              <Autocomplete
                className="w-100"
                id="autocomplete-modelos"
                options={modelos}
                getOptionLabel={(option) => option?.displayTitle || option?.nombreSelected || option?.nombre || ""}
                loading={loading}
                onInputChange={handleInputChange}
                onChange={(event, selected) => {
                  setSelectedModelo(selected);
                  setSelectedMaterialCaja("");
                  setSelectedEsfera("");
                  setSelectedDiametro("");
                  if (!selected) {
                    setAvailableFilters({ materialesCaja: [], esferas: [], diametros: [] });
                  } else {
                    loadFilterOptions(selected);
                  }
                }}
                value={selectedModelo || null}
                filterOptions={(options) => options}
                noOptionsText={loading ? "Buscando…" : "No se encontraron modelos. Prueba con otros términos."}
                renderOption={(option) => (
                  <div style={{ cursor: "pointer", borderBottom: "1px solid #eee", width: "99%", whiteSpace: "normal" }}>
                    <TarjetaModelo modelo={option} hideSpecs />
                  </div>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Buscar por nombre, SKU, marca... (mín. 3 caracteres)"
                    variant="outlined"
                    style={{ width: "100%" }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: handleScroll,
                }}
              />
            </div>
          </div>

          <div className="row d-flex align-items-center">
            <div className="col-4 col-md-4 text-center my-2">
              <Autocomplete
                className="w-100 mr-5"
                options={["Usado", "Sin estrenar"]}
                value={selectedEstado || ""}
                onChange={(event, selected) => setSelectedEstado(selected)}
                renderInput={(params) => (
                  <TextField {...params} margin="normal" variant="outlined" InputLabelProps={{ shrink: true }} label="Estado" />
                )}
              />
            </div>
            <div className="col-4 col-md-4 text-center my-2">
              <Autocomplete
                className="w-100 mr-5"
                options={contenidoOptions}
                getOptionLabel={(option) => option?.text}
                value={selectedContenido || null}
                onChange={(event, selected) => setSelectedContenido(selected)}
                renderInput={(params) => (
                  <TextField {...params} margin="normal" variant="outlined" InputLabelProps={{ shrink: true }} label="Contenido" />
                )}
              />
            </div>
            {false && (
            <div className="col-4 col-md-4 text-center my-2">
              <Autocomplete
                className="w-100 mr-5"
                multiple
                options={["Europa", "EEUU", "Japón", "Otros"]}
                value={selectedZonas || []}
                onChange={(event, selected) => setSelectedZonas(selected)}
                renderInput={(params) => (
                  <TextField {...params} margin="normal" variant="outlined" InputLabelProps={{ shrink: true }} label="Zona" />
                )}
              />
            </div>
            )}
          </div>
          <div className="row d-flex align-items-center">
            <div className="col-4 col-md-4 text-center my-2">
              <Autocomplete
                className="w-100 mr-5"
                options={availableFilters.materialesCaja || []}
                value={selectedMaterialCaja || null}
                onChange={(event, selected) => setSelectedMaterialCaja(selected || "")}
                loading={loadingFilters}
                disabled={loadingFilters}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    label={loadingFilters ? "Material de caja (cargando…)" : "Material de caja"}
                  />
                )}
              />
            </div>
            <div className="col-4 col-md-4 text-center my-2">
              <Autocomplete
                className="w-100 mr-5"
                options={availableFilters.esferas || []}
                value={selectedEsfera || null}
                onChange={(event, selected) => setSelectedEsfera(selected || "")}
                loading={loadingFilters}
                disabled={loadingFilters}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    label={loadingFilters ? "Esfera (cargando…)" : "Esfera"}
                  />
                )}
              />
            </div>
            <div className="col-4 col-md-4 text-center my-2">
              <Autocomplete
                className="w-100 mr-5"
                options={availableFilters.diametros || []}
                value={selectedDiametro || null}
                onChange={(event, selected) => setSelectedDiametro(selected || "")}
                loading={loadingFilters}
                disabled={loadingFilters}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    label={loadingFilters ? "Diámetro (cargando…)" : "Diámetro"}
                  />
                )}
              />
            </div>
          </div>
          <div className="d-flex">
            <div className="col-md-12 text-right my-2">
              <Button className="btn btn-primary" onClick={handleTasar} disabled={!selectedModelo}>
                Tasar
              </Button>
            </div>
          </div>

          <div className="row d-flex align-items-center">
            {tasacion && (
              <Card className="col-md-12 my-3">
                <CardContent>
                  <div className="d-flex flex-column align-items-center mb-6">
                    <Typography variant="h6" gutterBottom>{tasacion.modelo}</Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {tasacion.estado} - {tasacion.contenido}
                      {(selectedMaterialCaja || selectedEsfera || selectedDiametro) && (
                        <span>
                          {" "}- {[selectedMaterialCaja, selectedEsfera, selectedDiametro].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </Typography>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={4} className="text-right">
                      <Typography variant="h6" color="textSecondary">Valor Mínimo: {tasacion.min}€</Typography>
                    </Grid>
                    <Grid item xs={4} className="text-center">
                      <Typography variant="h6" color="primary">Precio Medio: {tasacion.avg}€</Typography>
                    </Grid>
                    <Grid item xs={4} className="text-left">
                      <Typography variant="h6" color="textSecondary">Valor Máximo: {tasacion.max}€</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
}
